import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { CheckIcon, ShieldAlert } from "lucide-react";

const Stepper = ({
  steps,
  currentStep,
  onStepperClick,
  hideNumber,
  errorSteps
}: {
  steps: { id: number; label: string; hide?: boolean; displayId?: string }[];
  currentStep: number;
  onStepperClick?: (val: number) => void | null;
  hideNumber?: boolean;
  errorSteps?: any;
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="grid grid-cols-1 mx-auto w-full max-w-screen-2xl flex-wrap min-w-[150px] gap-3">
        {steps.map((step, index) => {
          const isErrorStep = errorSteps?.includes(String(index));
          return (
            <div
              className={cn(
                "flex flex-row items-center gap-2 justify-start cursor-pointer",
                step?.hide ? "hidden" : "block"
              )}
              key={index}
              onClick={() => onStepperClick && onStepperClick(index + 1)}
            >
              {/* Step Circle */}
              <div
                className={cn(
                  "flex items-center justify-center w-[30px] h-[30px] rounded-full text-xs font-bold transition-all duration-300 ease-out",
                  currentStep === step.id || step.id < currentStep
                    ? "bg-primary text-white scale-105"
                    : "bg-gray-300 text-gray-700",
                    isErrorStep ? "bg-red-600" : ""
                )}
              >
                <div
                  className={cn(
                    "transition-transform duration-300 ease-out opacity-100"
                  )}
                >
                  {isErrorStep ? <ShieldAlert className="h-4 w-4 transition-all duration-300" /> : hideNumber ? (
                    step.label
                  ) : step.id < currentStep ? (
                    <CheckIcon className="h-4 w-4 transition-all duration-300" />
                  ) : (
                    step.displayId || step.id
                  )}
                </div>
              </div>
              {/* Step Label */}
              {hideNumber ? (
                <></>
              ) : (
                <span
                  className={cn(
                    "font-medium text-xs transition-colors duration-300",
                    currentStep === step.id || step.id < currentStep
                      ? "text-primary"
                      : "text-gray-600",
                      isErrorStep ? "text-red-600" : ""
                  )}
                >
                  {step.label}
                </span>
              )}
            </div>
          )})
        }
      </div>
    );
  } else {
    return (
      <div className="flex items-center mx-auto w-full max-w-screen-2xl flex-wrap min-w-[150px] gap-3">
        {steps.map((step, index) => {
          const isErrorStep = errorSteps?.includes(String(index));
          return (
            <div
              className={cn(
                "flex flex-col items-start gap-2 justify-center cursor-pointer",
                step?.hide ? "hidden" : "block"
              )}
              key={index}
              onClick={() => onStepperClick && onStepperClick(index + 1)}
            >
              <div key={step.id} className="flex items-start gap-3">
                {/* Step Circle */}
                <div
                  className={cn(
                    "flex items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ease-out",
                    currentStep === step.id || step.id < currentStep
                      ? "bg-primary text-white scale-105"
                      : "bg-gray-300 text-gray-700",
                    hideNumber ? "p-3" : "w-[30px] h-[30px]",
                    isErrorStep ? "bg-red-600" : ""
                  )}
                >
                  <div
                    className={cn(
                      "transition-transform duration-300 ease-out opacity-100"
                    )}
                  >
                    {isErrorStep ? <ShieldAlert  className="h-4 w-4 transition-all duration-300" /> : hideNumber ? (
                      step.label
                    ) : step.id < currentStep ? (
                      <CheckIcon className="h-4 w-4 transition-all duration-300" />
                    ) : (
                      step.displayId || step.id
                    )}
                  </div>
                </div>

                {/* Line Separator */}
                {index !== steps.length - 1 && (
                  <div
                    className={cn(
                      "flex-1 h-[2px] w-[90px] mt-[15px] transition-all duration-300 ease-out",
                      step.id < currentStep ? "bg-primary" : "bg-gray-300"
                    )}
                  ></div>
                )}
              </div>
              {/* Step Label */}
              {hideNumber ? (
                <></>
              ) : (
                <span
                  className={cn(
                    "font-medium text-xs transition-colors duration-300 text-center",
                    step.label?.length > 10 ? "ml-[-20px]" : step.label?.length < 5 ? "" : "ml-[-10px]",
                    currentStep === step.id || step.id < currentStep
                      ? "text-primary"
                      : "text-gray-600",
                    isErrorStep ? "text-red-600" : ""
                  )}
                >
                  {step.label}
                </span>
              )}
            </div>
          )}
        )}
      </div>
    );
  }
};

export default Stepper;
